/**
 * Custom hook for managing async data fetching with loading and error states.
 *
 * Eliminates the repeated pattern of:
 *   const [data, setData] = useState<T | null>(null);
 *   const [loading, setLoading] = useState(true);
 *   const [error, setError] = useState<string | null>(null);
 *
 * Usage:
 *   const { data, loading, error, setData, refetch } = useFetchData<MyType>(
 *     async () => {
 *       const response = await api.getData();
 *       return response.data;
 *     },
 *     { initialLoading: true }
 *   );
 */

import { useState, useCallback, useEffect, useRef } from 'react';

export interface FetchDataOptions {
  /** Start in loading state (default: true) */
  initialLoading?: boolean;
  /** Fetch data immediately on mount (default: true) */
  fetchOnMount?: boolean;
  /** Dependencies that trigger refetch when changed */
  deps?: unknown[];
}

export interface FetchDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  setData: React.Dispatch<React.SetStateAction<T | null>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => Promise<void>;
}

/**
 * Extracts a human-readable error message from Axios errors, DRF responses, or plain strings.
 *
 * @param err - The caught error (Axios error, Error instance, or string)
 * @param defaultMessage - Fallback message when no meaningful message can be extracted
 */
export function getErrorMessage(err: unknown, defaultMessage = 'An error occurred'): string {
  if (!err) return defaultMessage;

  // Handle Axios-style errors
  if (typeof err === 'object' && err !== null) {
    const error = err as Record<string, unknown>;

    // Check for response.data.detail (DRF format)
    const response = error['response'] as Record<string, unknown> | undefined;
    const data = response?.['data'] as Record<string, unknown> | undefined;

    if (typeof data?.['detail'] === 'string') {
      return data['detail'];
    }

    // Check for response.data.message (standardized API format)
    if (typeof data?.['message'] === 'string') {
      return data['message'];
    }

    // Check for response.data.error
    if (typeof data?.['error'] === 'string') {
      return data['error'];
    }

    // Check for message property
    if (typeof error['message'] === 'string') {
      return error['message'];
    }
  }

  // Handle string errors
  if (typeof err === 'string') {
    return err;
  }

  return defaultMessage;
}

/**
 * Manages async data fetching with loading, error, and refetch state.
 * Replaces the common useState triple (data, loading, error) pattern.
 *
 * @param fetchFn - Async function that returns the data
 * @param options - Control initial loading state, auto-fetch on mount, and dependency-driven refetch
 */
export function useFetchData<T>(
  fetchFn: () => Promise<T>,
  options: FetchDataOptions = {}
): FetchDataResult<T> {
  const {
    initialLoading = true,
    fetchOnMount = true,
    deps = [],
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState<string | null>(null);

  // Keep track of whether component is mounted
  const isMounted = useRef(true);

  // Store the fetch function in a ref to avoid dependency issues
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchFnRef.current();
      if (isMounted.current) {
        setData(result);
      }
    } catch (err) {
      if (isMounted.current) {
        setError(getErrorMessage(err, 'Failed to load data'));
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;

    if (fetchOnMount) {
      refetch();
    }

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    data,
    loading,
    error,
    setData,
    setError,
    setLoading,
    refetch,
  };
}

/**
 * Hook for managing multiple fetch states (useful for pages with multiple data sources)
 */
export interface MultiFetchState {
  loading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  startLoading: () => void;
  stopLoading: () => void;
}

/**
 * Manages loading and error state for pages that coordinate multiple independent fetches.
 *
 * @param initialLoading - Whether to start in a loading state (default: true)
 */
export function useMultiFetchState(initialLoading = true): MultiFetchState {
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState<string | null>(null);

  const startLoading = useCallback(() => {
    setLoading(true);
    setError(null);
  }, []);

  const stopLoading = useCallback(() => {
    setLoading(false);
  }, []);

  return {
    loading,
    error,
    setError,
    startLoading,
    stopLoading,
  };
}

export default useFetchData;
